// NGE-841 (task 5) — best-effort clean exit. On tab close/navigate-away, the
// socket connection just drops and the events-service backend has to wait out
// its ~45s ping-timeout before detecting the disconnect (see design doc §5.1).
// A synchronous sendBeacon logout on pagehide shortens that to ~0s for the
// common case (clean tab close), while staying correct without it — this is
// purely a latency optimization, not a correctness requirement.

import {
  UserLogoutEvent,
  MeetingLeaveEvent,
  UserCameraActivityEvent,
  UserMicActivityEvent,
  createEventEnvelope,
} from 'nexgen-types';
import { GlobalSocketService } from './socket.service';
import { useWorkplaceStore, useMeetingStore } from '@host/stores';
import { useGuestStore } from '@host/stores/guest.store';
import { useSessionStore } from '@host/stores/session.store';
import { getDeviceId, DEVICE_KIND } from './device-id.service';
import { isActivityTrackingEnabled } from './activity-tracking-flag.util';
import { jitsiService } from '@/shared/singletons/jitsi-singleton';

const EVENT_API_ROOT = import.meta.env.VITE_EVENT_API_ROOT || 'http://localhost:5550';
const EMIT_ENDPOINT = `${EVENT_API_ROOT}/api/events/emit`;

let registered = false;

export function sendBeacon(envelope: unknown): void {
  // `text/plain` deliberately, not `application/json` — the latter isn't a
  // CORS-safelisted content type, so the browser would need to run a
  // preflight OPTIONS request first. sendBeacon can't wait for that during
  // page teardown, so an `application/json` beacon can silently never reach
  // the server. The events service's body-parser is configured to accept
  // `text/plain` as JSON too, so the payload itself is unchanged.
  const blob = new Blob([JSON.stringify(envelope)], { type: 'text/plain' });
  // sendBeacon is fire-and-forget and, unlike fetch, is guaranteed by the
  // browser to survive page teardown — the only transport suitable here.
  navigator.sendBeacon(EMIT_ENDPOINT, blob);
}

function sendFinalLogoutBeacon(): void {
  if (!isActivityTrackingEnabled()) return;

  try {
    const user_pid = useWorkplaceStore.getState().user?.pid;
    if (!user_pid) return; // guest / not logged in — nothing to attribute

    const org_pid = useWorkplaceStore.getState().org?.pid;
    const floor_pid = useWorkplaceStore.getState().currentRoom?.floor_pid;
    const session_id = useSessionStore.getState().sessionId ?? undefined;

    const event = new UserLogoutEvent({
      user_pid,
      org_pid,
      floor_pid,
      session_id,
      device_id: getDeviceId(),
      device_kind: DEVICE_KIND,
    });

    // org_pid isn't part of the envelope shape (EnvelopeMeta) — it already
    // lives inside the event's own payload above.
    const envelope = createEventEnvelope(event, { user_pid, session_id });
    sendBeacon(envelope);
  } catch (error) {
    console.error('[ActivityCleanExit] Failed to send final logout beacon:', error);
  }
}

/**
 * A page reload/crash while in a meeting (or with camera/mic on) never runs
 * the normal leave/toggle-off code paths (leaveMeeting()'s
 * flushCameraMicOffOnLeave, or the mute-button handlers) — those only fire on
 * an explicit in-app action. Without a final "leave"/"off" here, the interval
 * for that dimension stays open in the metrics pipeline indefinitely (capped
 * only by the 30-min stale-connection backstop), which is what made
 * meeting/camera/mic seconds look like they kept growing forever across
 * reloads. This mirrors sendFinalLogoutBeacon() for those three dimensions.
 */
function sendFinalMeetingMediaBeacons(): void {
  if (!isActivityTrackingEnabled()) return;

  try {
    const authedUserPid = useWorkplaceStore.getState().user?.pid;
    const guest_pid = authedUserPid ? undefined : useGuestStore.getState().visitDetails?.guest?.pid;
    const user_pid = authedUserPid ?? guest_pid;
    if (!user_pid) return;

    const org_pid = useWorkplaceStore.getState().org?.pid;
    const floor_pid = useWorkplaceStore.getState().currentRoom?.floor_pid;
    const session_id = useSessionStore.getState().sessionId ?? undefined;
    const device_id = getDeviceId();

    const meetingState = useMeetingStore.getState();
    if (meetingState.currentRoomId && !meetingState.isMeetingHungUp) {
      const roomPid = meetingState.getCurrentRoomPid();
      const leaveEvent = new MeetingLeaveEvent({
        user_pid,
        guest_pid,
        meeting_pid: roomPid || user_pid,
        room_pid: roomPid || undefined,
        floor_pid,
        org_pid,
        session_id,
        device_id,
        device_kind: DEVICE_KIND,
      });
      sendBeacon(createEventEnvelope(leaveEvent, { user_pid, session_id, org_pid }));
    }

    if (jitsiService?.isCameraOn()) {
      const cameraEvent = new UserCameraActivityEvent({
        user_pid,
        guest_pid,
        status: 'off',
        org_pid,
        floor_pid,
        timestamp: new Date().toISOString(),
        session_id,
        device_id,
        device_kind: DEVICE_KIND,
      });
      sendBeacon(createEventEnvelope(cameraEvent, { user_pid, session_id, org_pid }));
    }

    if (jitsiService?.isMicOn()) {
      const micEvent = new UserMicActivityEvent({
        user_pid,
        guest_pid,
        status: 'off',
        org_pid,
        session_id,
        device_id,
        device_kind: DEVICE_KIND,
      });
      sendBeacon(createEventEnvelope(micEvent, { user_pid, session_id, org_pid }));
    }
  } catch (error) {
    console.error('[ActivityCleanExit] Failed to send final meeting/media beacons:', error);
  }
}

// pagehide and beforeunload can both fire for the same tab close/navigation,
// which without a guard means a duplicate socket.destroy() and duplicate
// logout/meeting-media beacons. Reset on pageshow so a page restored from
// bfcache (persisted:true) can still fire clean-exit again on its next hide.
let pageHideHandled = false;

function handlePageHide(): void {
  if (pageHideHandled) return;
  pageHideHandled = true;

  try {
    GlobalSocketService.destroy();
  } catch (error) {
    console.error('[ActivityCleanExit] Failed to disconnect socket on pagehide:', error);
  }
  sendFinalMeetingMediaBeacons();
  sendFinalLogoutBeacon();
}

function handlePageShow(event: PageTransitionEvent): void {
  if (event.persisted) {
    pageHideHandled = false;
  }
}

export function registerActivityCleanExit(): void {
  if (registered || typeof window === 'undefined') return;
  registered = true;

  // pagehide fires reliably on both tab close and navigation (including bfcache
  // cases beforeunload can miss); beforeunload is kept as a fallback for older
  // browsers that don't support pagehide.
  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('beforeunload', handlePageHide);
  window.addEventListener('pageshow', handlePageShow);
}
